import { useNavigate } from 'react-router-dom'
import {
  DeleteOutlined,
  EllipsisOutlined,
  EyeOutlined,
  ShareAltOutlined,
} from '../../components/icons'
import { useToast } from '../../components/ui/ToastProvider'
import { RowActionsMenuPortal, useRowActionsMenu } from '../../components/ui/rowActionsMenu'

export interface OrderRowMenuProps {
  readonly orderId: string
  /** A human-readable order label for the trigger's `aria-label` and the
   * Share/Delete toast copy — never the raw id. */
  readonly orderLabel: string
}

/**
 * Per-row "⋮ more actions" overflow menu (UID-132), matching the design's
 * row-level menu (`Fictiv Platform.dc.html` home `ordersTable` ~L472-483:
 * a trigger button + a View/Share/Delete popover). Shared by `OrderTable`
 * and `RecentOrders` so the menu's contents/behavior never drift between the
 * two surfaces.
 *
 * - **View** is real: navigates to the order-detail route (the same
 *   destination the row's own ORDER-cell link already points at).
 * - **Share** is real: copies the order-detail URL to the clipboard
 *   (mirrors `ActionBar`'s existing "copy link" behavior on the detail
 *   screen itself, so the same action reads the same way in both places).
 * - **Delete** is INERT — there is no order-removal/archival endpoint
 *   anywhere in the contract (orders are an immutable placed record, not a
 *   draft a buyer can discard), so this shows an informational toast
 *   instead of fabricating a destructive action with no backend behind it.
 *   A disclosed gap, not a bug.
 *
 * Open/close, dismissal, and the (portaled, overflow-safe) positioning live in
 * the shared {@link useRowActionsMenu} hook; only the order-specific View/Share/
 * Delete items differ here (and in the sibling `RfqRowMenu`). Accessibility: a
 * plain disclosure widget, not an ARIA APG menu — `aria-expanded`/`aria-controls`
 * only, closes on Escape (refocusing the trigger) and outside pointer-down.
 */
export function OrderRowMenu({ orderId, orderLabel }: OrderRowMenuProps) {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { open, coords, containerRef, triggerRef, panelRef, menuId, toggleOpen, runAndClose } =
    useRowActionsMenu()

  return (
    <div className="row-actions-menu" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="row-actions-menu-trigger"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`More actions for ${orderLabel}`}
        onClick={(event) => {
          event.stopPropagation()
          toggleOpen()
        }}
      >
        <EllipsisOutlined aria-hidden="true" />
      </button>
      <RowActionsMenuPortal open={open} coords={coords} menuId={menuId} panelRef={panelRef}>
        <button type="button" onClick={runAndClose(() => navigate(`/orders/${orderId}`))}>
          <EyeOutlined aria-hidden="true" />
          View
        </button>
        <button
          type="button"
          onClick={runAndClose(() => {
            void copyOrderLink(orderId, showToast)
          })}
        >
          <ShareAltOutlined aria-hidden="true" />
          Share
        </button>
        <hr className="row-actions-menu-divider" />
        <button
          type="button"
          className="row-actions-menu-item-danger"
          onClick={runAndClose(() =>
            showToast(`Removing “${orderLabel}” isn’t available yet.`, 'info'),
          )}
        >
          <DeleteOutlined aria-hidden="true" />
          Delete
        </button>
      </RowActionsMenuPortal>
    </div>
  )
}

async function copyOrderLink(
  orderId: string,
  showToast: (message: string, tone?: 'success' | 'error' | 'info') => void,
) {
  const url = `${window.location.origin}/orders/${orderId}`
  try {
    await navigator.clipboard.writeText(url)
    showToast('Link copied to clipboard', 'success')
  } catch {
    showToast("Couldn't copy the link. Try again.", 'error')
  }
}
